import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { shopsService } from '../services/firebase/shops.service';
import { Shop, ShopFormData, CoverageRequirement, WeeklyCoverage } from '../types/shop.types';
import { DayHours, WeeklyHours, DayOfWeek } from '../types/common.types';

/**
 * Custom hook for managing shops
 * Provides CRUD operations with React Query for caching and optimistic updates
 */
export function useShops() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.organizationId;

  // Fetch all shops
  const {
    data: shops = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['shops', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.getShops(organizationId);
    },
    enabled: !!organizationId,
  });

  // Create shop mutation
  const createShopMutation = useMutation({
    mutationFn: (data: ShopFormData) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.createShop(organizationId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Update shop mutation
  const updateShopMutation = useMutation({
    mutationFn: ({ shopId, data }: { shopId: string; data: Partial<ShopFormData> }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateShop(organizationId, shopId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Delete shop mutation
  const deleteShopMutation = useMutation({
    mutationFn: (shopId: string) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.deleteShop(organizationId, shopId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Update default hours mutation
  const updateDefaultHoursMutation = useMutation({
    mutationFn: ({ shopId, defaultHours }: { shopId: string; defaultHours: WeeklyHours }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateDefaultHours(organizationId, shopId, defaultHours);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Update date hours mutation
  const updateDateHoursMutation = useMutation({
    mutationFn: ({ shopId, date, hours }: { shopId: string; date: string; hours: DayHours }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateDateHours(organizationId, shopId, date, hours);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Remove date hours mutation
  const removeDateHoursMutation = useMutation({
    mutationFn: ({ shopId, date }: { shopId: string; date: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.removeDateHours(organizationId, shopId, date);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Update default coverage mutation
  const updateDefaultCoverageMutation = useMutation({
    mutationFn: ({ shopId, defaultCoverage }: { shopId: string; defaultCoverage: WeeklyCoverage }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateDefaultCoverage(organizationId, shopId, defaultCoverage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Helper functions
  const createShop = async (data: ShopFormData): Promise<string> => {
    return createShopMutation.mutateAsync(data);
  };

  const updateShop = async (shopId: string, data: Partial<ShopFormData>): Promise<void> => {
    return updateShopMutation.mutateAsync({ shopId, data });
  };

  const deleteShop = async (shopId: string): Promise<void> => {
    return deleteShopMutation.mutateAsync(shopId);
  };

  const updateDefaultHours = async (shopId: string, defaultHours: WeeklyHours): Promise<void> => {
    return updateDefaultHoursMutation.mutateAsync({ shopId, defaultHours });
  };

  const updateDateHours = async (shopId: string, date: string, hours: DayHours): Promise<void> => {
    return updateDateHoursMutation.mutateAsync({ shopId, date, hours });
  };

  const removeDateHours = async (shopId: string, date: string): Promise<void> => {
    return removeDateHoursMutation.mutateAsync({ shopId, date });
  };

  const updateDefaultCoverage = async (shopId: string, defaultCoverage: WeeklyCoverage): Promise<void> => {
    return updateDefaultCoverageMutation.mutateAsync({ shopId, defaultCoverage });
  };

  const getShop = (shopId: string): Shop | undefined => {
    return shops.find((shop) => shop.id === shopId);
  };

  // Coverage management mutations
  const addCoverageToDayMutation = useMutation({
    mutationFn: ({ shopId, day, coverage }: { shopId: string; day: DayOfWeek; coverage: CoverageRequirement }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.addCoverageToDay(organizationId, shopId, day, coverage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const removeCoverageFromDayMutation = useMutation({
    mutationFn: ({ shopId, day, coverageId }: { shopId: string; day: DayOfWeek; coverageId: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.removeCoverageFromDay(organizationId, shopId, day, coverageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const updateCoverageMutation = useMutation({
    mutationFn: ({ shopId, day, coverageId, updates }: { shopId: string; day: DayOfWeek; coverageId: string; updates: Partial<Omit<CoverageRequirement, 'id'>> }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateCoverage(organizationId, shopId, day, coverageId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const moveCoverageMutation = useMutation({
    mutationFn: ({ shopId, fromDay, toDay, coverageId }: { shopId: string; fromDay: DayOfWeek; toDay: DayOfWeek; coverageId: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.moveCoverage(organizationId, shopId, fromDay, toDay, coverageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const copyCoverageMutation = useMutation({
    mutationFn: ({ shopId, fromDay, toDay, coverageId }: { shopId: string; fromDay: DayOfWeek; toDay: DayOfWeek; coverageId: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.copyCoverage(organizationId, shopId, fromDay, toDay, coverageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Coverage helper functions
  const addCoverageToDay = async (shopId: string, day: DayOfWeek, coverage: CoverageRequirement): Promise<void> => {
    return addCoverageToDayMutation.mutateAsync({ shopId, day, coverage });
  };

  const removeCoverageFromDay = async (shopId: string, day: DayOfWeek, coverageId: string): Promise<void> => {
    return removeCoverageFromDayMutation.mutateAsync({ shopId, day, coverageId });
  };

  const updateCoverage = async (shopId: string, day: DayOfWeek, coverageId: string, updates: Partial<Omit<CoverageRequirement, 'id'>>): Promise<void> => {
    return updateCoverageMutation.mutateAsync({ shopId, day, coverageId, updates });
  };

  const moveCoverage = async (shopId: string, fromDay: DayOfWeek, toDay: DayOfWeek, coverageId: string): Promise<void> => {
    return moveCoverageMutation.mutateAsync({ shopId, fromDay, toDay, coverageId });
  };

  const copyCoverage = async (shopId: string, fromDay: DayOfWeek, toDay: DayOfWeek, coverageId: string): Promise<void> => {
    return copyCoverageMutation.mutateAsync({ shopId, fromDay, toDay, coverageId });
  };

  // Date-specific coverage mutations
  const addCoverageToDateMutation = useMutation({
    mutationFn: ({ shopId, date, coverage }: { shopId: string; date: string; coverage: CoverageRequirement }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.addCoverageToDate(organizationId, shopId, date, coverage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const removeCoverageFromDateMutation = useMutation({
    mutationFn: ({ shopId, date, coverageId }: { shopId: string; date: string; coverageId: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.removeCoverageFromDate(organizationId, shopId, date, coverageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
      // Also invalidate schedules since they depend on shop coverage
      queryClient.invalidateQueries({ queryKey: ['schedules', organizationId] });
    },
  });

  const updateCoverageOnDateMutation = useMutation({
    mutationFn: ({ shopId, date, coverageId, updates }: { shopId: string; date: string; coverageId: string; updates: Partial<Omit<CoverageRequirement, 'id'>> }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateCoverageOnDate(organizationId, shopId, date, coverageId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const removeDateCoverageMutation = useMutation({
    mutationFn: ({ shopId, date }: { shopId: string; date: string }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.removeDateCoverage(organizationId, shopId, date);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  const updateDateCoverageMutation = useMutation({
    mutationFn: ({ shopId, date, coverage }: { shopId: string; date: string; coverage: CoverageRequirement[] }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.updateDateCoverage(organizationId, shopId, date, coverage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Batch update date coverage mutation
  const batchUpdateShopCoverageMutation = useMutation({
    mutationFn: ({ shopId, updates }: { shopId: string; updates: Record<string, CoverageRequirement[] | "delete"> }) => {
      if (!organizationId) throw new Error('No organization ID');
      return shopsService.batchUpdateShopCoverage(organizationId, shopId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops', organizationId] });
    },
  });

  // Date-specific coverage helper functions
  const addCoverageToDate = async (shopId: string, date: string, coverage: CoverageRequirement): Promise<void> => {
    return addCoverageToDateMutation.mutateAsync({ shopId, date, coverage });
  };

  const removeCoverageFromDate = async (shopId: string, date: string, coverageId: string): Promise<void> => {
    return removeCoverageFromDateMutation.mutateAsync({ shopId, date, coverageId });
  };

  const updateCoverageOnDate = async (shopId: string, date: string, coverageId: string, updates: Partial<Omit<CoverageRequirement, 'id'>>): Promise<void> => {
    return updateCoverageOnDateMutation.mutateAsync({ shopId, date, coverageId, updates });
  };

  const removeDateCoverage = async (shopId: string, date: string): Promise<void> => {
    return removeDateCoverageMutation.mutateAsync({ shopId, date });
  };

  const updateDateCoverage = async (shopId: string, date: string, coverage: CoverageRequirement[]): Promise<void> => {
    return updateDateCoverageMutation.mutateAsync({ shopId, date, coverage });
  };

  const batchUpdateShopCoverage = async (shopId: string, updates: Record<string, CoverageRequirement[] | "delete">): Promise<void> => {
    return batchUpdateShopCoverageMutation.mutateAsync({ shopId, updates });
  };

  return {
    // Data
    shops,
    isLoading,
    error,

    // Functions
    createShop,
    updateShop,
    deleteShop,
    updateDefaultHours,
    updateDateHours,
    removeDateHours,
    updateDefaultCoverage,
    getShop,
    refetch,

    // Coverage management functions (default coverage)
    addCoverageToDay,
    removeCoverageFromDay,
    updateCoverage,
    moveCoverage,
    copyCoverage,

    // Date-specific coverage management functions
    addCoverageToDate,
    removeCoverageFromDate,
    updateCoverageOnDate,
    removeDateCoverage,
    updateDateCoverage,
    batchUpdateShopCoverage,

    // Mutation states
    isCreating: createShopMutation.isPending,
    isUpdating: updateShopMutation.isPending,
    isDeleting: deleteShopMutation.isPending,
  };
}
